import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Languages,
  Leaf,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useLoginContext } from "../../Contexts/LoginContext";
import {
  legacyPasswordLogin,
  ONBOARDING_IDENTIFIER_KEY,
  ONBOARDING_TOKEN_KEY,
  exchangeFirebaseToken,
  passwordRegister,
} from "../../services/authService";
import {
  clearFirebaseSession,
  completeFirebaseEmailLink,
  confirmFirebasePhoneOtp,
  createPhoneRecaptcha,
  getStoredFirebaseEmail,
  isFirebaseEmailSignInLink,
  requestFirebasePhoneOtp,
  resetPhoneRecaptcha,
  safelyClearPhoneRecaptcha,
  sendFirebaseEmailLink,
} from "../../services/firebaseAuth";
import {
  createPhoneOtpDeadline,
  formatPhoneOtpTime,
  getPhoneOtpSecondsRemaining,
  isFirebasePhoneOtpSessionError,
} from "../../utils/otpSession";
import {
  INDIAN_MOBILE_PATTERN,
  normalizeIndianMobileInput,
} from "../../utils/phoneNumber";
import GoogleAuthentication from "../reusable/GoogleAuthentication";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTP_LENGTH = 6;

const normalizeIdentifier = (channel, value) =>
  channel === "phone"
    ? normalizeIndianMobileInput(value)
    : value.trim().toLowerCase();

const maskIdentifier = (channel, value) => {
  if (channel === "phone") {
    return value.length >= 4 ? `••••••${value.slice(-4)}` : value;
  }

  const [name, domain] = value.split("@");
  if (!domain) return value;
  return `${name.slice(0, 1)}•••@${domain}`;
};

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const getFirebaseError = (error, t, fallbackKey) => {
  const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
  if (apiMessage) return apiMessage;

  if (
    error?.code === "auth/captcha-check-failed" &&
    /hostname match not found/i.test(String(error?.message || ""))
  ) {
    return t("auth.firebase_hostname_error", {
      hostname: window.location.hostname || "this hostname",
    });
  }

  const errorKeys = {
    "firebase/configuration-missing": "auth.firebase_configuration_error",
    "auth/invalid-verification-code": "auth.firebase_invalid_code",
    "auth/code-expired": "auth.otp_expired",
    "auth/invalid-verification-id": "auth.otp_expired",
    "auth/missing-verification-id": "auth.otp_expired",
    "auth/session-expired": "auth.otp_expired",
    "auth/invalid-action-code": "auth.firebase_link_invalid",
    "auth/expired-action-code": "auth.firebase_link_invalid",
    "auth/too-many-requests": "auth.firebase_too_many_requests",
    "auth/quota-exceeded": "auth.firebase_too_many_requests",
    "auth/unauthorized-domain": "auth.firebase_unauthorized_domain",
    "auth/app-not-authorized": "auth.firebase_unauthorized_domain",
    "auth/operation-not-allowed": "auth.firebase_phone_disabled",
    "auth/network-request-failed": "auth.firebase_network_error",
    "auth/captcha-check-failed": "auth.firebase_recaptcha_error",
    "auth/missing-recaptcha-token": "auth.firebase_recaptcha_error",
  };

  return t(errorKeys[error?.code] || fallbackKey);
};

function AuthPage({ mode = "login" }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, loggedIn } = useLoginContext();
  const isRegister = mode === "register";
  const emailLinkPending = useMemo(
    () => isFirebaseEmailSignInLink(window.location.href),
    [],
  );
  const [channel, setChannel] = useState(
    emailLinkPending ? "email" : "phone",
  );
  const [screen, setScreen] = useState("otp");
  const [step, setStep] = useState(
    emailLinkPending ? "email-link" : "identifier",
  );
  const [identifier, setIdentifier] = useState(() =>
    emailLinkPending ? getStoredFirebaseEmail() : "",
  );
  const [maskedIdentifier, setMaskedIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [registrationName, setRegistrationName] = useState("");
  const [registrationRole, setRegistrationRole] = useState("");
  const [registrationLanguage, setRegistrationLanguage] = useState(() =>
    i18n.resolvedLanguage?.toLowerCase().startsWith("hi") ? "hi" : "en",
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [phoneOtpExpiresAt, setPhoneOtpExpiresAt] = useState(null);
  const [phoneOtpSecondsRemaining, setPhoneOtpSecondsRemaining] = useState(0);
  const [phoneOtpExpired, setPhoneOtpExpired] = useState(false);
  const recaptchaVerifierRef = useRef(null);
  const phoneConfirmationRef = useRef(null);
  const firebaseIdTokenRef = useRef(null);

  const isHindi = i18n.resolvedLanguage?.toLowerCase().startsWith("hi");
  const normalizedIdentifier = useMemo(
    () => normalizeIdentifier(channel, identifier),
    [channel, identifier],
  );

  const clearPhoneRecaptcha = useCallback(() => {
    const verifier = recaptchaVerifierRef.current;
    recaptchaVerifierRef.current = null;
    safelyClearPhoneRecaptcha(verifier);
  }, []);

  const expirePhoneOtpSession = useCallback((message = "") => {
    phoneConfirmationRef.current = null;
    firebaseIdTokenRef.current = null;
    setPhoneOtpExpiresAt(null);
    setPhoneOtpSecondsRemaining(0);
    setPhoneOtpExpired(true);
    setCode("");
    setResendIn(0);
    if (message) setError(message);
    clearFirebaseSession().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (loggedIn) navigate("/", { replace: true });
  }, [loggedIn, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = window.setInterval(
      () => setResendIn((seconds) => Math.max(0, seconds - 1)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [resendIn]);

  useEffect(() => {
    if (!phoneOtpExpiresAt) return undefined;

    const updateTimeRemaining = () => {
      const secondsRemaining = getPhoneOtpSecondsRemaining(phoneOtpExpiresAt);
      setPhoneOtpSecondsRemaining(secondsRemaining);

      if (secondsRemaining === 0) {
        expirePhoneOtpSession(t("auth.otp_expired"));
      }
    };

    updateTimeRemaining();
    const timer = window.setInterval(updateTimeRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [expirePhoneOtpSession, phoneOtpExpiresAt, t]);

  useEffect(
    () => () => clearPhoneRecaptcha(),
    [clearPhoneRecaptcha],
  );

  const changeLanguage = async () => {
    const language = isHindi ? "en" : "hi";
    await i18n.changeLanguage(language);
    setRegistrationLanguage(language);
    localStorage.setItem("i18nextLng", language);
    document.documentElement.lang = language;
  };

  const validateIdentifier = () => {
    if (
      channel === "phone" &&
      !INDIAN_MOBILE_PATTERN.test(normalizedIdentifier)
    ) {
      return t("auth.invalid_phone");
    }
    if (channel === "email" && !EMAIL_PATTERN.test(normalizedIdentifier)) {
      return t("auth.invalid_email");
    }
    return "";
  };

  const resetFirebaseProof = () => {
    clearPhoneRecaptcha();
    phoneConfirmationRef.current = null;
    firebaseIdTokenRef.current = null;
    setPhoneOtpExpiresAt(null);
    setPhoneOtpSecondsRemaining(0);
    setPhoneOtpExpired(false);
    clearFirebaseSession().catch(() => undefined);
  };

  const selectChannel = (nextChannel) => {
    resetFirebaseProof();
    const shouldCompleteEmailLink = nextChannel === "email" && emailLinkPending;
    setChannel(nextChannel);
    setIdentifier(shouldCompleteEmailLink ? getStoredFirebaseEmail() : "");
    setMaskedIdentifier("");
    setCode("");
    setStep(shouldCompleteEmailLink ? "email-link" : "identifier");
    setResendIn(0);
    setError("");
  };

  const finishFirebaseAuthentication = async (idToken, verifiedChannel) => {
    const data = await exchangeFirebaseToken({
      channel: verifiedChannel,
      idToken,
    });

    if (data?.onboardingRequired) {
      if (!data.onboardingToken) throw new Error("Missing onboarding token");
      await clearFirebaseSession();
      firebaseIdTokenRef.current = null;
      sessionStorage.setItem(ONBOARDING_TOKEN_KEY, data.onboardingToken);
      sessionStorage.setItem(
        ONBOARDING_IDENTIFIER_KEY,
        data.maskedIdentifier || maskedIdentifier,
      );
      navigate("/onboarding", { replace: true });
      return;
    }

    if (!data?.token) throw new Error("Missing application token");

    await clearFirebaseSession();
    firebaseIdTokenRef.current = null;
    sessionStorage.removeItem(ONBOARDING_TOKEN_KEY);
    sessionStorage.removeItem(ONBOARDING_IDENTIFIER_KEY);
    login(data.token);
    navigate("/", { replace: true });
  };

  const sendCode = async () => {
    const validationError = validateIdentifier();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setIsSubmitting(true);
    let phoneVerifier = null;
    try {
      firebaseIdTokenRef.current = null;
      phoneConfirmationRef.current = null;
      setPhoneOtpExpiresAt(null);
      setPhoneOtpSecondsRemaining(0);
      setPhoneOtpExpired(channel === "phone" && step === "code");
      setResendIn(0);
      setMaskedIdentifier(maskIdentifier(channel, normalizedIdentifier));

      if (channel === "email") {
        await sendFirebaseEmailLink(normalizedIdentifier);
        setStep("email-sent");
        setResendIn(60);
      } else {
        await clearFirebaseSession().catch(() => undefined);
        phoneVerifier = recaptchaVerifierRef.current;

        if (phoneVerifier) {
          await resetPhoneRecaptcha(phoneVerifier);
        } else {
          const recaptchaAnchorId =
            step === "code"
              ? "firebase-phone-resend"
              : "firebase-phone-submit";
          phoneVerifier = createPhoneRecaptcha(
            recaptchaAnchorId,
            isHindi ? "hi" : "en",
          );
          recaptchaVerifierRef.current = phoneVerifier;
        }

        phoneConfirmationRef.current = await requestFirebasePhoneOtp(
          `+91${normalizedIdentifier}`,
          phoneVerifier,
        );
        clearPhoneRecaptcha();
        setStep("code");
        setCode("");
        const expiresAt = createPhoneOtpDeadline();
        setPhoneOtpExpiresAt(expiresAt);
        setPhoneOtpSecondsRemaining(
          getPhoneOtpSecondsRemaining(expiresAt),
        );
        setPhoneOtpExpired(false);
        setResendIn(60);
      }
    } catch (requestError) {
      console.error("Firebase OTP request failed:", requestError);
      if (channel === "phone") {
        await resetPhoneRecaptcha(
          phoneVerifier || recaptchaVerifierRef.current,
        ).catch(() => undefined);
      }
      if (channel === "phone" && step === "code") {
        expirePhoneOtpSession();
      }
      setError(
        getFirebaseError(
          requestError,
          t,
          channel === "phone"
            ? "auth.request_error"
            : "auth.email_link_request_error",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestSubmit = (event) => {
    event.preventDefault();
    sendCode();
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();
    if (
      phoneOtpExpired ||
      !phoneOtpExpiresAt ||
      getPhoneOtpSecondsRemaining(phoneOtpExpiresAt) === 0
    ) {
      expirePhoneOtpSession(t("auth.otp_expired"));
      return;
    }

    if (!new RegExp(`^\\d{${OTP_LENGTH}}$`).test(code)) {
      setError(code ? t("auth.code_invalid") : t("auth.code_required"));
      return;
    }

    setError("");
    setIsSubmitting(true);
    let firebaseConfirmationCompleted = false;
    try {
      const confirmationResult = phoneConfirmationRef.current;
      if (!confirmationResult) {
        const missingSessionError = new Error(
          "Phone verification session is missing",
        );
        missingSessionError.code = "auth/missing-verification-id";
        throw missingSessionError;
      }

      const idToken = await confirmFirebasePhoneOtp(confirmationResult, code);
      firebaseConfirmationCompleted = true;
      phoneConfirmationRef.current = null;
      firebaseIdTokenRef.current = idToken;
      setPhoneOtpExpiresAt(null);
      setPhoneOtpSecondsRemaining(0);
      await finishFirebaseAuthentication(idToken, "phone");
    } catch (verifyError) {
      if (firebaseConfirmationCompleted) {
        expirePhoneOtpSession(t("auth.firebase_session_invalid"));
      } else if (isFirebasePhoneOtpSessionError(verifyError)) {
        expirePhoneOtpSession(t("auth.otp_expired"));
      } else {
        setError(getFirebaseError(verifyError, t, "auth.verify_error"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailLinkSubmit = async (event) => {
    event.preventDefault();
    const email = identifier.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setError(t("auth.invalid_email"));
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      let idToken = firebaseIdTokenRef.current;
      if (!idToken) {
        idToken = await completeFirebaseEmailLink(
          email,
          window.location.href,
        );
        firebaseIdTokenRef.current = idToken;
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
      setMaskedIdentifier(maskIdentifier("email", email));
      await finishFirebaseAuthentication(idToken, "email");
    } catch (emailError) {
      setError(getFirebaseError(emailError, t, "auth.firebase_link_invalid"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = () => {
    if (!isSubmitting && (phoneOtpExpired || resendIn === 0)) sendCode();
  };

  const changeIdentifier = () => {
    resetFirebaseProof();
    setStep("identifier");
    setCode("");
    setResendIn(0);
    setError("");
  };

  const showLegacyLogin = () => {
    resetFirebaseProof();
    setScreen("legacy");
    setChannel("phone");
    setIdentifier("");
    setStep("identifier");
    setCode("");
    setError("");
  };

  const selectMethod = (method) => {
    if (method === "password") {
      showLegacyLogin();
      return;
    }
    setScreen("otp");
    selectChannel(method);
    setPassword("");
    setConfirmPassword("");
  };

  const handleLegacySubmit = async (event) => {
    event.preventDefault();
    const mobile = normalizeIndianMobileInput(identifier);
    if (!INDIAN_MOBILE_PATTERN.test(mobile)) {
      setError(t("auth.invalid_phone"));
      return;
    }
    if (!password) {
      setError(t("auth.password_required"));
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      const data = await legacyPasswordLogin({
        username: mobile,
        password,
      });
      if (!data?.token) {
        setError(t("auth.login_error"));
        return;
      }
      login(data.token);
      navigate("/", { replace: true });
    } catch (loginError) {
      setError(getApiError(loginError, t("auth.login_error")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordRegisterSubmit = async (event) => {
    event.preventDefault();
    const mobile = normalizeIndianMobileInput(identifier);
    if (registrationName.trim().length < 2) {
      setError(t("onboarding.name_required"));
      return;
    }
    if (!INDIAN_MOBILE_PATTERN.test(mobile)) {
      setError(t("auth.invalid_phone"));
      return;
    }
    if (password.length < 6) {
      setError(t("auth.password_too_short"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("auth.passwords_mismatch"));
      return;
    }
    if (!registrationRole) {
      setError(t("onboarding.role_required"));
      return;
    }
    if (!acceptedTerms) {
      setError(t("onboarding.terms_required"));
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      const data = await passwordRegister({
        name: registrationName.trim(),
        username: mobile,
        password,
        role: registrationRole,
        language: registrationLanguage,
        acceptedTerms,
      });
      if (!data?.token) {
        setError(t("auth.register_error"));
        return;
      }
      login(data.token);
      navigate("/", { replace: true });
    } catch (registerError) {
      setError(getApiError(registerError, t("auth.register_error")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = isRegister ? t("auth.register_title") : t("auth.login_title");
  const subtitle = isRegister
    ? t("auth.register_subtitle")
    : t("auth.login_subtitle");
  const passwordTitle = isRegister
    ? t("auth.password_register_title")
    : t("auth.password_login_title");
  const passwordSubtitle = isRegister
    ? t("auth.password_register_subtitle")
    : t("auth.password_login_subtitle");

  return (
    <main className="min-h-screen w-full bg-gradient-to-br from-indigo-50 via-white to-emerald-50 px-4 py-6 text-[#1A2233] sm:py-10">
      <div className="mx-auto flex w-full max-w-5xl items-start justify-between">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          aria-label={t("auth.back_home")}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">{t("auth.back_home")}</span>
        </button>
        <button
          type="button"
          onClick={changeLanguage}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          aria-label={t("language.change")}
        >
          <Languages className="h-4 w-4" aria-hidden="true" />
          {isHindi ? "English" : "हिंदी"}
        </button>
      </div>

      <section className="mx-auto mt-6 w-full max-w-md rounded-3xl border border-white/70 bg-white p-6 shadow-xl shadow-indigo-100/70 sm:p-8">
        <header className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-200">
            <ShieldCheck className="h-7 w-7" aria-hidden="true" />
          </div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-indigo-600">
            {t("auth.brand")}
          </p>
          <h1 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            {screen === "legacy" ? passwordTitle : title}
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {screen === "legacy" ? passwordSubtitle : subtitle}
          </p>
        </header>

        <div
          id="auth-error"
          className={`mt-5 rounded-xl border px-4 py-3 text-sm ${
            error
              ? "border-red-200 bg-red-50 text-red-700"
              : "sr-only"
          }`}
          role="status"
          aria-live="polite"
        >
          {error}
        </div>

        <div
          className="mt-6 grid grid-cols-3 rounded-xl bg-slate-100 p-1"
          role="tablist"
          aria-label={t("auth.method_label")}
        >
          {[
            { value: "phone", label: t("auth.phone_tab"), Icon: Phone },
            { value: "email", label: t("auth.email_tab"), Icon: Mail },
            {
              value: "password",
              label: t("auth.password_tab"),
              Icon: LockKeyhole,
            },
          ].map(({ value, label, Icon }) => {
            const selected = value === "password"
              ? screen === "legacy"
              : screen === "otp" && channel === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => selectMethod(value)}
                className={`flex min-h-12 flex-col items-center justify-center gap-1 rounded-lg px-1.5 py-2 text-[11px] font-bold transition sm:flex-row sm:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  selected
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>

        {screen === "otp" ? (
          <>
            {step === "identifier" ? (
              <form className="mt-6 space-y-5" onSubmit={handleRequestSubmit}>
                <div>
                  <label
                    htmlFor="auth-identifier"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    {channel === "phone"
                      ? t("auth.phone_label")
                      : t("auth.email_label")}
                  </label>
                  <div className="relative">
                    {channel === "phone" ? (
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-sm font-semibold text-slate-500">
                        +91
                      </span>
                    ) : (
                      <Mail
                        className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                        aria-hidden="true"
                      />
                    )}
                    <input
                      id="auth-identifier"
                      type={channel === "phone" ? "tel" : "email"}
                      inputMode={channel === "phone" ? "numeric" : "email"}
                      autoComplete={channel === "phone" ? "tel" : "email"}
                      value={identifier}
                      onChange={(event) => {
                        const value =
                          channel === "phone"
                            ? normalizeIndianMobileInput(event.target.value)
                            : event.target.value;
                        setIdentifier(value);
                        setError("");
                      }}
                      placeholder={
                        channel === "phone"
                          ? t("auth.phone_placeholder")
                          : t("auth.email_placeholder")
                      }
                      className={`min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pr-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                        channel === "phone" ? "pl-14" : "pl-12"
                      }`}
                      aria-describedby={error ? "auth-error" : undefined}
                    />
                  </div>
                </div>
                {channel === "phone" && (
                  <p className="text-xs leading-5 text-slate-500">
                    {t("auth.phone_consent")}
                  </p>
                )}
                <button
                  id={channel === "phone" ? "firebase-phone-submit" : undefined}
                  type="submit"
                  disabled={isSubmitting}
                  className="flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting
                    ? channel === "phone"
                      ? t("auth.sending_code")
                      : t("auth.sending_email_link")
                    : channel === "phone"
                      ? t("auth.send_code")
                      : t("auth.send_email_link")}
                </button>
              </form>
            ) : step === "code" ? (
              <form className="mt-6 space-y-5" onSubmit={handleVerifySubmit}>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  <div className="flex items-start gap-2">
                    <CheckCircle2
                      className="mt-0.5 h-4 w-4 shrink-0"
                      aria-hidden="true"
                    />
                    <p>{t("auth.code_sent", { destination: maskedIdentifier })}</p>
                  </div>
                </div>
                <p
                  id="otp-session-status"
                  role="status"
                  aria-live="polite"
                  className={`rounded-xl border px-4 py-3 text-center text-sm font-semibold ${
                    phoneOtpExpired
                      ? "border-amber-200 bg-amber-50 text-amber-900"
                      : "border-indigo-100 bg-indigo-50 text-indigo-800"
                  }`}
                >
                  {phoneOtpExpired
                    ? t("auth.otp_expired")
                    : t("auth.otp_expires_in", {
                        time: formatPhoneOtpTime(phoneOtpSecondsRemaining),
                      })}
                </p>
                <div>
                  <label
                    htmlFor="otp-code"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    {t("auth.code_label")}
                  </label>
                  <input
                    id="otp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={OTP_LENGTH}
                    value={code}
                    disabled={phoneOtpExpired || isSubmitting}
                    onChange={(event) => {
                      setCode(
                        event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH),
                      );
                      setError("");
                    }}
                    placeholder={t("auth.code_placeholder")}
                    aria-describedby="otp-session-status"
                    className="min-h-14 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-center text-2xl font-bold tracking-[0.45em] text-slate-900 outline-none transition placeholder:tracking-normal placeholder:text-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || phoneOtpExpired}
                  className="flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting
                    ? t("auth.verifying_code")
                    : t("auth.verify_code")}
                </button>
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <button
                    type="button"
                    onClick={changeIdentifier}
                    className="font-semibold text-slate-600 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {t("auth.change_identifier")}
                  </button>
                  <button
                    id="firebase-phone-resend"
                    type="button"
                    disabled={
                      (!phoneOtpExpired && resendIn > 0) || isSubmitting
                    }
                    onClick={handleResend}
                    className="font-semibold text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-400"
                  >
                    {phoneOtpExpired
                      ? t("auth.send_new_code")
                      : resendIn > 0
                      ? t("auth.resend_in", { seconds: resendIn })
                      : t("auth.resend_code")}
                  </button>
                </div>
              </form>
            ) : step === "email-sent" ? (
              <div className="mt-6 space-y-5">
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center text-emerald-900">
                  <CheckCircle2
                    className="mx-auto h-8 w-8 text-emerald-600"
                    aria-hidden="true"
                  />
                  <h2 className="mt-3 font-bold">
                    {t("auth.email_link_sent_title")}
                  </h2>
                  <p className="mt-2 text-sm leading-6">
                    {t("auth.email_link_sent", {
                      destination: maskedIdentifier,
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                  <button
                    type="button"
                    onClick={changeIdentifier}
                    className="font-semibold text-slate-600 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    {t("auth.change_email")}
                  </button>
                  <button
                    type="button"
                    disabled={resendIn > 0 || isSubmitting}
                    onClick={handleResend}
                    className="font-semibold text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-400"
                  >
                    {resendIn > 0
                      ? t("auth.resend_in", { seconds: resendIn })
                      : t("auth.resend_email_link")}
                  </button>
                </div>
              </div>
            ) : (
              <form
                className="mt-6 space-y-5"
                onSubmit={handleEmailLinkSubmit}
              >
                <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm leading-6 text-indigo-900">
                  {t("auth.email_link_confirm_help")}
                </div>
                <div>
                  <label
                    htmlFor="email-link-address"
                    className="mb-2 block text-sm font-semibold text-slate-700"
                  >
                    {t("auth.email_label")}
                  </label>
                  <div className="relative">
                    <Mail
                      className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      aria-hidden="true"
                    />
                    <input
                      id="email-link-address"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={identifier}
                      onChange={(event) => {
                        setIdentifier(event.target.value);
                        setError("");
                      }}
                      placeholder={t("auth.email_placeholder")}
                      className="min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pl-12 pr-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting
                    ? t("auth.completing_email_link")
                    : t("auth.complete_email_link")}
                </button>
              </form>
            )}

          </>
        ) : isRegister ? (
          <form
            className="mt-6 space-y-5"
            onSubmit={handlePasswordRegisterSubmit}
          >
            <div>
              <label
                htmlFor="password-register-name"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("onboarding.name_label")}
              </label>
              <div className="relative">
                <UserRound
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  id="password-register-name"
                  type="text"
                  autoComplete="name"
                  maxLength={100}
                  value={registrationName}
                  onChange={(event) => {
                    setRegistrationName(event.target.value);
                    setError("");
                  }}
                  placeholder={t("onboarding.name_placeholder")}
                  className="min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pl-12 pr-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password-register-phone"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("auth.phone_label")}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-sm font-semibold text-slate-500">
                  +91
                </span>
                <input
                  id="password-register-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={identifier}
                  onChange={(event) => {
                    setIdentifier(normalizeIndianMobileInput(event.target.value));
                    setError("");
                  }}
                  placeholder={t("auth.phone_placeholder")}
                  className="min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pl-14 pr-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>

            <fieldset>
              <legend className="text-sm font-semibold text-slate-700">
                {t("onboarding.language_label")}
              </legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { value: "en", label: t("onboarding.english") },
                  { value: "hi", label: t("onboarding.hindi") },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={registrationLanguage === option.value}
                    onClick={async () => {
                      setRegistrationLanguage(option.value);
                      await i18n.changeLanguage(option.value);
                      localStorage.setItem("i18nextLng", option.value);
                      document.documentElement.lang = option.value;
                      setError("");
                    }}
                    className={`min-h-11 rounded-xl border px-3 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                      registrationLanguage === option.value
                        ? "border-indigo-600 bg-indigo-50 text-indigo-800"
                        : "border-slate-300 text-slate-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="text-sm font-semibold text-slate-700">
                {t("onboarding.role_label")}
              </legend>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  {
                    value: "admin",
                    label: t("onboarding.dairy_owner"),
                    Icon: Building2,
                  },
                  {
                    value: "user",
                    label: t("onboarding.farmer"),
                    Icon: Leaf,
                  },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={registrationRole === value}
                    onClick={() => {
                      setRegistrationRole(value);
                      setError("");
                    }}
                    className={`flex min-h-12 items-center justify-center gap-2 rounded-xl border px-2 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                      registrationRole === value
                        ? "border-indigo-600 bg-indigo-50 text-indigo-800"
                        : "border-slate-300 text-slate-700"
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label
                htmlFor="password-register-password"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("auth.password_label")}
              </label>
              <div className="relative">
                <LockKeyhole
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  id="password-register-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setError("");
                  }}
                  placeholder={t("auth.password_placeholder")}
                  className="min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pl-12 pr-12 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-500 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  aria-label={
                    showPassword
                      ? t("auth.hide_password")
                      : t("auth.show_password")
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="password-register-confirm"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("auth.confirm_password_label")}
              </label>
              <input
                id="password-register-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setError("");
                }}
                placeholder={t("auth.confirm_password_placeholder")}
                className="min-h-12 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-5 text-slate-700">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => {
                  setAcceptedTerms(event.target.checked);
                  setError("");
                }}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                {t("onboarding.terms_prefix")} {" "}
                <Link
                  to="/PrivacyPolicy"
                  target="_blank"
                  className="font-bold text-indigo-700 hover:underline"
                >
                  {t("onboarding.privacy_policy")}
                </Link>
              </span>
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? t("auth.password_register_submitting")
                : t("auth.password_register_submit")}
            </button>
          </form>
        ) : (
          <form className="mt-6 space-y-5" onSubmit={handleLegacySubmit}>
            <div>
              <label
                htmlFor="legacy-phone"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("auth.phone_label")}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-sm font-semibold text-slate-500">
                  +91
                </span>
                <input
                  id="legacy-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="username"
                  value={identifier}
                  onChange={(event) => {
                    setIdentifier(normalizeIndianMobileInput(event.target.value));
                    setError("");
                  }}
                  placeholder={t("auth.phone_placeholder")}
                  className="min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pl-14 pr-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="legacy-password"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                {t("auth.password_label")}
              </label>
              <div className="relative">
                <LockKeyhole
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  id="legacy-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    setError("");
                  }}
                  placeholder={t("auth.password_placeholder")}
                  className="min-h-12 w-full rounded-xl border border-slate-300 bg-white py-3 pl-12 pr-12 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-500 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  aria-label={
                    showPassword
                      ? t("auth.hide_password")
                      : t("auth.show_password")
                  }
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Eye className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex min-h-12 w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 font-bold text-white shadow-md shadow-indigo-200 transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? t("auth.legacy_submitting")
                : t("auth.legacy_submit")}
            </button>
          </form>
        )}

        {(step === "identifier" || screen === "legacy") && (
          <div className="mt-6">
            <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              {t("auth.google_separator")}
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="mt-4 flex justify-center">
              <GoogleAuthentication />
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-sm text-slate-600">
          {isRegister ? t("auth.have_account") : t("auth.no_account")} {" "}
          <Link
            to={isRegister ? "/login" : "/register"}
            className="font-bold text-indigo-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            {isRegister ? t("auth.login_here") : t("auth.create_account")}
          </Link>
        </p>
      </section>
    </main>
  );
}

export default AuthPage;
